import type { Dispatch, SetStateAction } from "react";

/**
 * Reusable, dependency-free validation system.
 *
 * No React Hook Form / Formik / Yup / Zod — just plain functions that
 * plug into a page's own `useState<Record<string, string>>({})` errors
 * state. Every Add/Edit page (Quotation, Invoice, Delivery Order, and
 * any future module) builds its own field/row rules out of these
 * primitives and stays in full control of its own business logic.
 */

export type FieldErrors = Record<string, string>;

/** A single-field validator: receives the field's current value, returns an error message or undefined. */
export type Validator<V> = (value: V) => string | undefined;

/* ------------------------------------------------------------------ */
/*  Primitive validators                                               */
/* ------------------------------------------------------------------ */

export function validateRequired(
  value: unknown,
  message = "This field is required"
): string | undefined {
  if (value === null || value === undefined) return message;
  if (typeof value === "string" && value.trim() === "") return message;
  if (typeof value === "number" && Number.isNaN(value)) return message;
  return undefined;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Validates format only. Combine with `validateRequired` via `composeValidators` if the field is mandatory. */
export function validateEmail(
  value: string,
  message = "Invalid email address"
): string | undefined {
  if (!value) return undefined;
  return EMAIL_REGEX.test(value) ? undefined : message;
}

export function validateNumberRange(
  value: number,
  min: number,
  max: number,
  message?: string
): string | undefined {
  if (Number.isNaN(value)) return message ?? `Must be a number between ${min} and ${max}`;
  if (value < min || value > max) {
    return message ?? `Must be between ${min} and ${max}`;
  }
  return undefined;
}

export function validateMin(
  value: number,
  min: number,
  message?: string
): string | undefined {
  if (Number.isNaN(value) || value < min) {
    return message ?? `Must be at least ${min}`;
  }
  return undefined;
}

/** Convenience wrapper for the common "must be greater than 0" rule. */
export function validatePositive(value: number, message = "Must be greater than 0"): string | undefined {
  return value > 0 ? undefined : message;
}

/** Chains validators for a single field — the first one to return a message wins. */
export function composeValidators<V>(
  ...validators: Array<Validator<V>>
): Validator<V> {
  return (value: V) => {
    for (const validator of validators) {
      const message = validator(value);
      if (message) return message;
    }
    return undefined;
  };
}

/* ------------------------------------------------------------------ */
/*  Flat field-object validation (recipient_name, email, sales_id, ...) */
/* ------------------------------------------------------------------ */



export type ValidatorOrArray<T> =
  | Validator<T>
  | Validator<T>[];

export function runFieldValidation<T extends Record<string, any>>(
  value: T,
  validators: Partial<{ [K in keyof T]: ValidatorOrArray<T[K]> }>
): FieldErrors {

  
  const errors: FieldErrors = {};

  (Object.keys(validators) as (keyof T)[]).forEach((key) => {
    const rule = validators[key];

    if (!rule) return;

    const validatorList = Array.isArray(rule) ? rule : [rule];

    for (const validator of validatorList) {
      const message = validator(value[key]);

      if (message) {
        errors[key as string] = message;
        break; // Stop at the first validation error
      }
    }
  });

  return errors;
}

/* ------------------------------------------------------------------ */
/*  Item-table (row) validation                                        */
/* ------------------------------------------------------------------ */

export type RowErrors = Record<string, string>;
export type ItemsErrors = Record<number, RowErrors>;

export interface ItemsValidationResult {
  itemsErrors: ItemsErrors;
  hasError: boolean;
  /** Set when the whole list is empty (e.g. "at least one item is required") */
  generalError?: string;
}

/**
 * Validates every row of an item table against a per-column rule map.
 * Works for Quotation/Invoice items (with price) and Delivery Order
 * items (without price) alike — just pass the columns that apply.
 */
export function validateItems<T extends Record<string, any>>(
  items: T[],
  rules: Partial<{ [K in keyof T]: (value: T[K], row: T, index: number) => string | undefined }>,
  emptyMessage = "At least one item is required"
): ItemsValidationResult {
  if (!items || items.length === 0) {
    return { itemsErrors: {}, hasError: true, generalError: emptyMessage };
  }

  const itemsErrors: ItemsErrors = {};
  let hasError = false;

  items.forEach((row, index) => {
    const rowErrors: RowErrors = {};
    (Object.keys(rules) as (keyof T)[]).forEach((key) => {
      const rule = rules[key];
      if (!rule) return;
      const message = rule(row[key], row, index);
      if (message) {
        rowErrors[key as string] = message;
        hasError = true;
      }
    });
    if (Object.keys(rowErrors).length > 0) {
      itemsErrors[index] = rowErrors;
    }
  });

  return { itemsErrors, hasError, generalError: undefined };
}

/** Small factory helpers so page-level rule maps stay one-liners. */
export const requiredText = (message = "This field is required") => (value: unknown) =>
  validateRequired(value, message);

export const positiveNumber = (message = "Must be greater than 0") => (value: number) =>
  validatePositive(value, message);

/* ------------------------------------------------------------------ */
/*  Wiring errors into DocumentInformation's onChange                  */
/* ------------------------------------------------------------------ */

/**
 * Wraps a page's onChange handler so that editing a field clears that
 * field's error immediately (real-time validation), without the page
 * having to repeat this logic in every switch-case.
 */
export function withErrorClear<T>(
  onChange: <K extends keyof T>(key: K, value: T[K]) => void,
  setErrors: Dispatch<SetStateAction<FieldErrors>>
) {
  return <K extends keyof T>(key: K, value: T[K]) => {
    setErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
    onChange(key, value);
  };
}

/** Clears the error for a single item-table cell (row index + column key). */
export function clearItemError(
  setItemsErrors: Dispatch<SetStateAction<ItemsErrors>>,
  index: number,
  key: string
) {
  setItemsErrors((prev) => {
    if (!prev[index]?.[key]) return prev;
    const nextRow = { ...prev[index] };
    delete nextRow[key];
    const next = { ...prev };
    if (Object.keys(nextRow).length === 0) {
      delete next[index];
    } else {
      next[index] = nextRow;
    }
    return next;
  });
}

/* ------------------------------------------------------------------ */
/*  Focus / scroll behavior on submit                                   */
/* ------------------------------------------------------------------ */

/** Scrolls to and focuses the DocumentInformation field with the given key (expects id="field-<key>"). */
export function scrollToField(key: string) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(`field-${key}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (typeof (el as HTMLElement).focus === "function") {
    (el as HTMLElement).focus({ preventScroll: true });
  }
}

/** Scrolls to a named section (e.g. the item table or summary block) when the error isn't a DocumentInformation field. */
export function scrollToSection(sectionId: string) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(sectionId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Given the three possible error buckets a page can produce (field
 * errors, item errors, summary errors), scrolls/focuses the first one
 * in priority order: field -> items -> summary.
 */
export function focusFirstError(options: {
  fieldErrors: FieldErrors;
  itemsHasError?: boolean;
  itemsSectionId?: string;
  summaryHasError?: boolean;
  summarySectionId?: string;
}) {
  const firstFieldKey = Object.keys(options.fieldErrors)[0];
  if (firstFieldKey) {
    scrollToField(firstFieldKey);
    return;
  }
  if (options.itemsHasError && options.itemsSectionId) {
    scrollToSection(options.itemsSectionId);
    return;
  }
  if (options.summaryHasError && options.summarySectionId) {
    scrollToSection(options.summarySectionId);
  }
}
